import React, { useState, useEffect, useRef } from 'react';

function MetricChart({ data, height = 80, color = '#6366f1', fillOpacity = 0.15, showGrid = false, showLabels = false }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(300);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const noData = (
    <div ref={containerRef} className="flex items-center justify-center" style={{ height }}>
      <span className="muted" style={{ fontSize: 12 }}>No data</span>
    </div>
  );

  if (!data || data.length === 0) {
    return noData;
  }

  const points = data.map(d => ({ x: d[0], y: parseFloat(d[1]) })).filter(d => !isNaN(d.y));
  if (points.length === 0) {
    return noData;
  }

  const padding = showLabels ? { top: 5, right: 5, bottom: 20, left: 35 } : { top: 2, right: 2, bottom: 2, left: 2 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const minY = Math.min(...points.map(p => p.y));
  const maxY = Math.max(...points.map(p => p.y));
  const minX = points[0].x;
  const maxX = points[points.length - 1].x;

  const rangeY = maxY - minY || 1;
  const rangeX = maxX - minX || 1;

  const toX = (v) => padding.left + ((v - minX) / rangeX) * chartW;
  const toY = (v) => padding.top + chartH - ((v - minY) / rangeY) * chartH;

  const linePoints = points.map(p => `${toX(p.x)},${toY(p.y)}`).join(' ');
  const areaPoints = `${toX(points[0].x)},${padding.top + chartH} ${linePoints} ${toX(points[points.length - 1].x)},${padding.top + chartH}`;

  const yLabels = showLabels ? [minY, (minY + maxY) / 2, maxY].map(v => `${v.toFixed(0)}%`) : [];

  return (
    <div ref={containerRef} style={{ maxWidth: '100%', overflow: 'hidden' }}>
      <svg width={width} height={height} style={{ overflow: 'visible' }}>
        {showGrid && yLabels.map((label, i) => {
          const y = padding.top + chartH - (i / (yLabels.length - 1)) * chartH;
          return (
            <g key={i}>
              <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="var(--glass-border)" strokeWidth="0.5" />
              <text x={padding.left - 4} y={y + 3} textAnchor="end" fill="var(--text-3)" fontSize="9">{label}</text>
            </g>
          );
        })}
        <polygon points={areaPoints} fill={color} fillOpacity={fillOpacity} />
        <polyline points={linePoints} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

export default MetricChart;
